// src/components/admin/contract/ContractKpiDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContractKpis } from './ContractApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRupeeSign,
  faClockRotateLeft,
  faListCheck,
  faShieldHalved,
  faFileInvoice,
  faLock,
} from '@fortawesome/free-solid-svg-icons';

import { MODULES } from '../../constants/globalConstants';
import {
  readUserFromLocalStorage,
  hasPermission,          // hasPermission(user, moduleName, actions, 'any'|'all')
  getModulePermissionRow, // returns the module permission object for a user+module
} from '../../utils/permissions';
import type { UserDTO } from '../../components/auth/AuthApi';

interface KpiData {
  [key: string]:
    | number
    | null
    | { status: string; count: number }[]
    | { type?: string | null; deposit_type?: string; total: number }[];
}

const ContractKpiDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [kpis, setKpis] = useState<KpiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const user = readUserFromLocalStorage<UserDTO>();

  // Permission row for Contracts (to inspect visibleInDashboard)
  const contractsPermRow = useMemo(
    () => getModulePermissionRow(user || null, MODULES.CONTRACTS) || null,
    [user]
  );

  // Can see dashboard if (view OR report) AND (visibleInDashboard !== false)
  const canSeeDashboard = useMemo(() => {
    const viewOrReport = hasPermission(user || null, MODULES.CONTRACTS, ['view', 'report'], 'any');

    // If the flag exists, require it; otherwise default to true
    const visibleFlag =
      contractsPermRow && typeof contractsPermRow.visibleInDashboard === 'boolean'
        ? !!contractsPermRow.visibleInDashboard
        : true;

    return viewOrReport && visibleFlag;
  }, [contractsPermRow, user]);

  // Header buttons visibility
  const canViewEditOrCreate = useMemo(
    () => hasPermission(user || null, MODULES.CONTRACTS, ['view', 'edit', 'create'], 'any'),
    [user]
  );
  const canCreateContracts = useMemo(
    () => hasPermission(user || null, MODULES.CONTRACTS, 'create', 'any'),
    [user]
  );

  useEffect(() => {
    if (!canSeeDashboard) {
      setLoading(false);
      setKpis(null);
      setError('');
      return;
    }

    const loadKpis = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchContractKpis();
        setKpis(data);
      } catch (err) {
        setError('Failed to load KPI data');
      } finally {
        setLoading(false);
      }
    };

    loadKpis();
  }, [canSeeDashboard]);

  const renderValue = (value: any) => {
    if (typeof value === 'number') {
      return <span className="text-xl font-semibold">₹ {value.toLocaleString()}</span>;
    }
    if (Array.isArray(value)) {
      return (
        <ul className="list-disc pl-5 text-sm text-gray-600">
          {value.map((item, index) => (
            <li key={index}>
              {'status' in item && item.status && `${item.status}: ${item.count}`}
              {'type' in item && item.type && `${item.type || 'N/A'}: ₹ ${item.total.toLocaleString()}`}
              {'deposit_type' in item && item.deposit_type && `${item.deposit_type}: ₹ ${item.total.toLocaleString()}`}
            </li>
          ))}
        </ul>
      );
    }
    return <span className="text-gray-500">-</span>;
  };

  const iconMap: Record<string, any> = {
    total_security_deposit: faShieldHalved,
    total_refunded: faRupeeSign,
    pending_refund: faClockRotateLeft,
    refund_initiated: faClockRotateLeft,
    average_refund_days: faClockRotateLeft,
    paper_cost_rejected: faFileInvoice,
    paper_cost_awarded: faFileInvoice,
    paper_cost_pre_project: faFileInvoice,
    refunded_completed_project: faRupeeSign,
    pre_project_deposit: faShieldHalved,
    unawarded_projects_deposit: faShieldHalved,
    terminated_projects_deposit: faShieldHalved,
    dispute_projects_deposit: faShieldHalved,
    contract_count_by_status: faListCheck,
    paper_cost_by_contract_type: faFileInvoice,
    deposit_by_type: faShieldHalved,
  };

  // Unauthorized screen
  if (!canSeeDashboard) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto bg-white border rounded-xl p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <div className="w-12 h-12 rounded-full border flex items-center justify-center text-slate-600">
              <FontAwesomeIcon icon={faLock} />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-1">Access Restricted</h2>
          <p className="text-slate-600">
            You don’t have permission to view the Contracts dashboard. If you believe this is an
            error, please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  // Authorized screen
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Contract Dashboard KPIs</h2>

        {/* Header actions (role-gated) */}
        <div className="flex flex-wrap gap-2">
          {canViewEditOrCreate && (
            <>
              <button
                onClick={() => navigate('/contracts')}
                className="px-3 py-2 rounded border hover:bg-slate-50"
                title="View All Contracts"
              >
                View All Contracts
              </button>
              <button
                onClick={() => navigate('/contracts/deposits')}
                className="px-3 py-2 rounded border hover:bg-slate-50"
                title="Contract Deposit"
              >
                Contract Deposit
              </button>
            </>
          )}

          {canCreateContracts && (
            <>
              <button
                onClick={() => navigate('/contracts/create')}
                className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                title="Create Contract"
              >
                Create Contract
              </button>
              <button
                onClick={() => navigate('/contracts/bulk-upload')}
                className="px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                title="Bulk Upload"
              >
                Bulk Upload
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-600">Loading...</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {kpis &&
            Object.entries(kpis).map(([key, value]) => (
              <div
                key={key}
                className="bg-white p-4 rounded shadow hover:shadow-lg transition duration-200"
              >
                <div className="flex items-center mb-2">
                  {iconMap[key] && (
                    <FontAwesomeIcon icon={iconMap[key]} className="text-blue-500 mr-2" />
                  )}
                  <h3 className="text-lg font-semibold">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </h3>
                </div>
                <div>{renderValue(value)}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ContractKpiDashboard;